import { Box, Divider } from "@mui/material";
import TotalAccordion from "./TotalAccordion";
import BudgetToolbar from "../BudgetToolbar";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import {
  initialBudgetWideAppliedTaxes,
  NFMMOption,
} from "../../../../types/budget/budget";
import { formatCurrency } from "../../../../utils/formatters";
import { resolveServiceCalcOption } from "../../../../utils/budget-phase-calc-option";
import { filterServicesCountedInStageTotals } from "../../../../utils/budget-stage-visibility";
import {
  filterTotalsBlockToVisibleStage,
  sumVisibleGlobalField,
  getVisibleStageGrandTotal,
  sumVisibleBlockField,
  visibilityContextFromBudget,
} from "../../../../utils/visible-stage-totals";
import { useMemo } from "react";
import GlobalAppliedTaxes from "./GlobalAppliedTaxes";
import NotaSummary from "./NotaSummary";

interface BudgetTotalsProps {}

export default function BudgetTotals({}: BudgetTotalsProps) {
  const { activeBudgetIndex, activeBudget, versionInfo } = useBudget();
  const optionsArr: NFMMOption[] = ["A", "B", "C", "D"];

  const budgetWideAppliedTaxes = useMemo(() => {
    return (
      activeBudget.budgetWideAppliedTaxes?.find(
        (el) => el.version === versionInfo.version
      ) || initialBudgetWideAppliedTaxes
    );
  }, [activeBudget, versionInfo]);
  const extras = budgetWideAppliedTaxes?.taxData.extras.value || 0;
  const extrasType = budgetWideAppliedTaxes?.taxData.extras.typeTax;
  const versionTotals = useMemo(
    () =>
      getVisibleStageGrandTotal(
        activeBudget,
        versionInfo.status,
        versionInfo.version
      ),
    [activeBudget, versionInfo]
  );
  const visibilityCtx = visibilityContextFromBudget(
    activeBudget,
    versionInfo.status
  );

  const totalsWithExtras =
    extrasType === "PERCENTAGE"
      ? versionTotals + versionTotals * (extras / 100)
      : versionTotals + extras;

  // Totais por bloco (A/B/C/D + agregado SUM) para o Resumo de Nota.
  // Calculado direto no corpo do render (sem useMemo), igual ao "Total" do
  // TotalAccordion logo acima — evita os dois lerem valores diferentes se
  // activeBudget for atualizado sem trocar a referência do objeto todo.
  let byCalcType: typeof activeBudget.budgetValuesTotal.byCalculationType;
  if (versionInfo.version === 0) {
    byCalcType = activeBudget.budgetValuesTotal?.byCalculationType;
  } else if (versionInfo.status === "APPROVED") {
    byCalcType = activeBudget.closingValuesTotal?.byCalculationType;
  } else {
    byCalcType =
      activeBudget.negociationValuesTotal[versionInfo.version - 1]
        ?.byCalculationType;
  }

  const blockField = (block: NFMMOption, field: string) =>
    sumVisibleBlockField(
      byCalcType?.[block],
      block,
      versionInfo.version,
      field,
      visibilityCtx
    );
  const blockTariff = (block: NFMMOption) => blockField(block, "tariffTotals");
  const blockServiceTax = (block: NFMMOption) =>
    blockField(block, "serviceTaxTotals");
  const blockIss = (block: NFMMOption) => blockField(block, "issTotals");
  const blockCbs = (block: NFMMOption) => blockField(block, "cbsTotals");
  const blockIbs = (block: NFMMOption) => blockField(block, "ibsTotals");
  const blockIof = (block: NFMMOption) => blockField(block, "iofTotals");
  const blockMarkup = (block: NFMMOption) => blockField(block, "markupTotals");
  // Nota de Fornecedores soma Tarifa + Taxa de Serviço + ISS/CBS/IBS/IOF + Markup/Over do Bloco A.
  const notaFornecedores =
    blockTariff("A") +
    blockServiceTax("A") +
    blockIss("A") +
    blockCbs("A") +
    blockIbs("A") +
    blockIof("A") +
    blockMarkup("A");
  // Apontamento do financeiro: Nota de Débito também precisa considerar a
  // Taxa de Serviço, o ISS/CBS/IBS, o IOF e o Markup/Over do Bloco B, além da Tarifa/Convertido.
  const notaDebito =
    blockTariff("B") +
    blockServiceTax("B") +
    blockIss("B") +
    blockCbs("B") +
    blockIbs("B") +
    blockIof("B") +
    blockMarkup("B");
  const subtotalC =
    blockTariff("C") +
    blockServiceTax("C") +
    blockIss("C") +
    blockCbs("C") +
    blockIbs("C") +
    blockIof("C") +
    blockMarkup("C");
  const subtotalD =
    blockTariff("D") +
    blockServiceTax("D") +
    blockIss("D") +
    blockCbs("D") +
    blockIbs("D") +
    blockIof("D") +
    blockMarkup("D");
  // MMV-2659: Imposto MM e Taxa MM saem dos blocos FILTRADOS, não do agregado
  // `byCalculationType.SUM.SUM` gravado, que continua contando a categoria
  // excluída na etapa — no MM 23954.26 isso dobrava o imposto (230,02 em vez
  // de 115,01) e inflava a NOTA FISCAL, que soma imposto + taxa MM +
  // subtotais C e D. Árvore que só guarda esses campos no agregado mantém o
  // agregado (ver `sumVisibleGlobalField`).
  const imposto = sumVisibleGlobalField(
    byCalcType,
    versionInfo.version,
    "tributeTotals",
    visibilityCtx
  );
  const taxaMM = sumVisibleGlobalField(
    byCalcType,
    versionInfo.version,
    "feeTotals",
    visibilityCtx
  );

  return (
    <Box width={"100%"} gap={1}>
      <Box
        pb={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: "20px", fontWeight: "semi-bold" }}>
          Totais - {activeBudget.name || `Orçamento ${activeBudgetIndex + 1}`}
        </span>
        <BudgetToolbar />
      </Box>
      {optionsArr.map((el) => {
        // Mesma regra das células somadas: serviço movido na etapa conta no
        // bloco de destino, e só se o card do fornecedor existir lá (MMV-2626).
        const services = filterServicesCountedInStageTotals(
          activeBudget.budgetServices,
          versionInfo.version,
          versionInfo.status,
          activeBudget.supplierList,
          activeBudget.addedItemTypeList
        ).filter(
          (service) => resolveServiceCalcOption(service, versionInfo) === el
        );
        const rawCategoryTotal =
          versionInfo.version === 0
            ? activeBudget.budgetValuesTotal?.byCalculationType?.[el]
            : versionInfo.status === "APPROVED"
            ? activeBudget.closingValuesTotal?.byCalculationType?.[el]
            : activeBudget.negociationValuesTotal[versionInfo.version - 1]
                ?.byCalculationType?.[el];
        const categoryTotal = filterTotalsBlockToVisibleStage(
          rawCategoryTotal,
          el,
          versionInfo.version,
          visibilityCtx
        );
        if (services.length < 1 || el === "SUM" || !categoryTotal) return <></>;
        return (
          <TotalAccordion
            type={el}
            totalPerCalcOption={categoryTotal}
            services={services}
          />
        );
      })}

      <Divider />
      <GlobalAppliedTaxes />

      <Box my={2} gap={2}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          fontWeight={"bold"}
          my={1}
        >
          <Box>Total</Box>
          <Box>{formatCurrency(versionTotals)}</Box>
        </Box>
      </Box>
      <Box my={2} gap={2}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          fontWeight={"bold"}
          my={1}
        >
          <Box>Total + Extra</Box>
          <Box>{formatCurrency(totalsWithExtras)}</Box>
        </Box>
      </Box>

      {/* #MMV-2672: mesmo componente/layout do Fechamento, agora também em
          Orçamento e Negociação -- cálculo e storage do abatimento já são
          genéricos por versão, não havia nada específico de Fechamento
          além deste gate. */}
      <NotaSummary
        notaFornecedores={notaFornecedores}
        notaDebito={notaDebito}
        imposto={imposto}
        taxaMM={taxaMM}
        subtotalC={subtotalC}
        subtotalD={subtotalD}
      />
    </Box>
  );
}
